"use client"

import { useState, useCallback, useEffect, useMemo } from "react"
import { useState as useStateOriginal } from "react"
import {
  Paper,
  Box,
  Grid,
  Avatar,
  Typography,
  IconButton,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { ChevronDown, ChevronUp } from "lucide-react"
import FolderIcon from "@mui/icons-material/Folder"
import SquareIcon from "@mui/icons-material/SelectAll"
import PowerOffIcon from "@mui/icons-material/PowerSettingsNew"
import MapIcon from '@mui/icons-material/Map';
import ViewListIcon from '@mui/icons-material/ViewList';

import DeviceList from "./DeviceList"
import BottomMenu from "../common/components/BottomMenu"
import StatusCard from "../common/components/StatusCard"
import { devicesActions, sessionActions } from "../store"
import usePersistedState from "../common/util/usePersistedState"
import EventsDrawer from "./EventsDrawer"
import MainToolbar from "./MainToolbar"
import { useTranslation } from "../common/components/LocalizationProvider"
import MainMap from "./MainMap"
import { useAttributePreference } from "../common/util/preferences"
import { nativePostMessage } from "../common/components/NativeInterface"
import { useAdministrator, useManager } from "../common/util/permissions"

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "rgba(255, 255, 255, 0.9)",
  },
  sidebar: {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "rgba(255, 255, 255, 0.9)",
    pointerEvents: "none",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      position: "fixed",
      left: 0,
      top: 0,
      height: `calc(100% - ${theme.spacing(3)})`,
      width: 380,
      margin: theme.spacing(1.5),
      zIndex: 3,
    },
    [theme.breakpoints.down("md")]: {
      height: "100%",
      width: "100%",
    },
  },
  header: {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "rgba(255, 255, 255, 0.9)",
    pointerEvents: "auto",
    zIndex: 6,
    color: theme.palette.mode === "dark" ? theme.palette.text.primary : "#000000",
    "& .MuiIconButton-root": {
      color: theme.palette.mode === "dark" ? theme.palette.text.primary : "#000000",
    },
    "& .MuiTypography-root": {
      color: theme.palette.mode === "dark" ? theme.palette.text.primary : "#000000",
    },
  },
  footer: {
    pointerEvents: "auto",
    zIndex: 5,
  },
  middle: {
    flex: 1,
    display: "grid",
    [theme.breakpoints.up("md")]: {
      height: "calc(100% - 200px)",
    },
    [theme.breakpoints.down("md")]: {
      height: "calc(100% - 180px)",
    },
  },
  contentMap: {
    pointerEvents: "auto",
    gridArea: "1 / 1",
  },
  contentList: {
    pointerEvents: "auto",
    gridArea: "1 / 1",
    zIndex: 4,
  },
  bottomMenu: {
    "& .MuiBottomNavigation-root": {
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "rgba(255, 255, 255, 0.9)",
    },
    "& .MuiBottomNavigationAction-root": {
      color: theme.palette.mode === "dark" ? theme.palette.text.primary : "inherit",
      "&.success": {
        color: theme.palette.mode === "dark" ? "#4CAF50" : "#2e7d32",
      },
      "&.error": {
        color: theme.palette.mode === "dark" ? "#FF5252" : "#d32f2f",
      },
      "&.neutral": {
        color: theme.palette.mode === "dark" ? "#BDBDBD" : "#757575",
      },
      "&.warning": {
        color: theme.palette.mode === "dark" ? "#FFC107" : "#ed6c02",
      },
    },
  },
  userName: {
    color: theme.palette.mode === "dark" ? theme.palette.text.primary : "#fff",
  },
  userEmail: {
    color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.72)" : "rgba(255, 255, 255, 0.72)",
  },
  groupList: {
    width: "100%",
    padding: 0,
  },
}))

const getPhoto = (item) => {
  let filephoto = "default.png"
  if (item.photo) {
    if (item.photo.length > 0 && item.photo.length < 30) {
      filephoto = item.photo
    }
  }
  return `./photos/users/${filephoto}`
}

const MainPage = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const t = useTranslation()

  const desktop = useMediaQuery(theme.breakpoints.up("md"))
  const mapOnSelect = useAttributePreference("mapOnSelect", true)

  const selectedDeviceId = useSelector((state) => state.devices.selectedId)
  const positions = useSelector((state) => state.session.positions)
  const devices = useSelector((state) => state.devices.items)

  const [filteredGroups, setFilteredGroups] = useState([])
  const [selectedGroupId, setSelectedGroupId] = useState(-1)
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [groupsError, setGroupsError] = useState(null)
  const [groupsExpanded, setGroupsExpanded] = useStateOriginal(true)

  const [keyword, setKeyword] = useState("")
  const [filter, setFilter] = usePersistedState("filter", {
    statuses: [],
    groups: [],
  })
  const [filterSort, setFilterSort] = usePersistedState("filterSort", "")
  const [filterMap, setFilterMap] = usePersistedState("filterMap", false)
  const [searchFields, setSearchFields] = useState(["all"]); // Padrão para buscar em todos os campos

  const [devicesOpen, setDevicesOpen] = useState(desktop)
  const [mapOnlyMode, setMapOnlyMode] = useState(false)
  const [eventsOpen, setEventsOpen] = useState(false)

  const onEventsClick = useCallback(() => setEventsOpen(true), [setEventsOpen])

  useEffect(() => {
    if (!desktop && mapOnSelect && selectedDeviceId) {
      setDevicesOpen(false)
    }
  }, [desktop, mapOnSelect, selectedDeviceId])

  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true)
      setGroupsError(null)
      try {
        const response = await fetch("/api/groups")
        if (response.ok) {
          const data = await response.json()
          setFilteredGroups(data)
        } else {
          setGroupsError(`Error ${response.status}: ${response.statusText}`)
        }
      } catch (error) {
        setGroupsError(error.message)
      } finally {
        setGroupsLoading(false)
      }
    }
    fetchGroups()
  }, [])

  useEffect(() => {
    setFilter({ ...filter, groups: selectedGroupId === -1 ? [] : [selectedGroupId] })
  }, [selectedGroupId])

    const filteredDevices = useMemo(() => {
      let result = Object.values(devices);

      // Substituir o bloco de filtragem por keyword por:
      if (keyword && keyword.trim()) {
        const searchTerm = keyword.toLowerCase().trim();

        result = result.filter((device) => {
          // Campos padrão para busca
          const standardFields = {
            name: device.name || '',
            uniqueId: device.uniqueId || '',
            phone: device.phone || '',
            model: device.model || device.attributes?.form?.model || '',
            contact: device.contact || '',
            category: device.category || ''
          };

          // Campos personalizados (incluindo nested form)
          const customFields = {
            plate: device.attributes?.form?.plate || '',
            brand: device.attributes?.form?.brand || '',
            color: device.attributes?.form?.color || '',
            operator: device.attributes?.form?.operator || '',
            vendor: device.attributes?.form?.vendor || '',
            iccid: device.attributes?.form?.iccid || '',
            prefix: device.attributes?.form?.prefix || '',
            chassi: device.attributes?.form?.chassi || '',
            vin: device.attributes?.form?.vin || ''
          };

          // Busca combinada em todos os campos
          const allFields = {...standardFields, ...customFields};

          return Object.values(allFields).some(value =>
            String(value).toLowerCase().includes(searchTerm)
          );
        });
      }

      // Filtros adicionais (status, grupos)
      if (filter.statuses.length > 0) {
        result = result.filter(device => filter.statuses.includes(device.status));
      }

      if (filter.groups.length > 0) {
        result = result.filter(device => filter.groups.includes(device.groupId));
      }

      // Ordenação
      if (filterSort) {
        result.sort((a, b) => {
          if (filterSort === "name") return a.name?.localeCompare(b.name);
          if (filterSort === "lastUpdate") return new Date(b.lastUpdate) - new Date(a.lastUpdate);
          return 0;
        });
      }

      return result;
    }, [devices, keyword, filter, filterSort, searchFields]);

  const filteredPositions = useMemo(() => {
    let result = Object.values(positions)

    if (filterMap && filteredDevices.length > 0) {
      const deviceIds = filteredDevices.map((device) => device.id)
      result = result.filter((position) => deviceIds.includes(position.deviceId))
    }

    return result
  }, [positions, filterMap, filteredDevices])

  const selectedPosition = filteredPositions.find(
    (position) => selectedDeviceId && position.deviceId === selectedDeviceId,
  )

  const isAdmin = useAdministrator()
  const isManager = useManager()
  const currentUser = useSelector((state) => state.session.user)

  const handleLogout = async () => {
    const user = currentUser
    const notificationToken = window.localStorage.getItem("notificationToken")
    if (notificationToken && !user.readonly) {
      window.localStorage.removeItem("notificationToken")
      const tokens = user.attributes.notificationTokens?.split(",") || []
      if (tokens.includes(notificationToken)) {
        const updatedUser = {
          ...user,
          attributes: {
            ...user.attributes,
            notificationTokens:
              tokens.length > 1 ? tokens.filter((it) => it !== notificationToken).join(",") : undefined,
          },
        }
        await fetch(`/api/users/${user.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedUser),
        })
      }
    }

    await fetch("/api/session", { method: "DELETE" })
    nativePostMessage("logout")
    navigate("/login")
    dispatch(sessionActions.updateUser(null))
  }

  const handleSelectGroup = (groupId, event) => {
    event.stopPropagation()
    setSelectedGroupId(groupId)
  }

  const toggleGroupsExpanded = (event) => {
    event.stopPropagation()
    setGroupsExpanded(!groupsExpanded)
  }

  const GroupsList = () => {
    if (groupsLoading) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
            Loading...
          </Typography>
        </Box>
      )
    }

    if (groupsError) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "error.main", fontSize: "0.75rem" }}>
            Error: {groupsError}
          </Typography>
        </Box>
      )
    }

    if (!filteredGroups || filteredGroups.length === 0) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}></Typography>
        </Box>
      )
    }

    const rootGroups = Array.isArray(filteredGroups)
      ? filteredGroups.filter((g) => g.groupId === 0)
      : Object.values(filteredGroups).filter((g) => g.groupId === 0)

    return (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 0.5,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={toggleGroupsExpanded}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "0.7rem",
              fontWeight: 500,
            }}
          >
            {t("sharedGroups")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
              p: 0.5,
            }}
          >
            {groupsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Box>
        </Box>

        {groupsExpanded && (
          <Box
            sx={{
              maxHeight: "160px",
              overflowY: "auto",
              mt: 0.5,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <List className={classes.groupList} dense disablePadding>
              <ListItemButton
                selected={selectedGroupId === -1}
                onClick={(e) => handleSelectGroup(-1, e)}
                sx={{
                  py: 0.5,
                  px: 1,
                  minHeight: "28px",
                  borderRadius: 0,
                  "&.Mui-selected": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: "24px" }}>
                  <SquareIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("sharedAll")}
                  primaryTypographyProps={{
                    fontSize: "0.8rem",
                    fontWeight: 400,
                    color: "text.primary",
                  }}
                />
              </ListItemButton>

              {rootGroups.map((group) => (
                <ListItemButton
                  key={group.id}
                  selected={selectedGroupId === group.id}
                  onClick={(e) => handleSelectGroup(group.id, e)}
                  sx={{
                    py: 0.5,
                    px: 1,
                    minHeight: "28px",
                    borderRadius: 0,
                    "&.Mui-selected": {
                      backgroundColor:
                        theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "24px" }}>
                    <FolderIcon sx={{ fontSize: "0.9rem", color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={group.name}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      fontWeight: 400,
                      color: "text.primary",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </>
    )
  }

  return (
    <div className={classes.root}>
      {desktop && (
        <MainMap
          filteredPositions={filteredPositions}
          selectedPosition={selectedPosition}
          onEventsClick={onEventsClick}
        />
      )}
      {!mapOnlyMode && (
        <div className={classes.sidebar}>
        <Paper square elevation={3} className={classes.header} sx={{ pb: 1 }}>
          <Box sx={{ position: "relative", px: 2, pt: 1 }}>
            <IconButton
              onClick={handleLogout}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: theme.palette.mode === "dark" ? "text.primary" : "#fff",
                zIndex: 10,
              }}
            >
              <PowerOffIcon />
            </IconButton>
            <Grid container spacing={2} sx={{ width: "100%", cursor: "pointer" }}>
              <Grid sx={{ flex: "0 0 calc(16.666% - 16px)" }}>
                <Avatar
                  src={getPhoto({})}
                  onClick={() => {}}
                  style={{ marginTop: 10, marginLeft: 20 }}
                  sx={{
                    mx: "auto",
                    borderWidth: 1,
                    textAlign: { md: "center" },
                    borderStyle: "solid",
                    borderColor: "primary",
                    width: { xs: 48, md: 48 },
                    height: { xs: 48, md: 48 },
                  }}
                  name={currentUser.name || ""}
                />
              </Grid>
              <Grid
                sx={{
                  flex: "0 0 calc(66.666% - var(--Grid-spacing))",
                  maxWidth: "calc(66.666% - var(--Grid-spacing))",
                }}
              >
                <Box onClick={() => {}} style={{ marginTop: 15, marginLeft: 15 }}>
                  <Typography
                    variant="body1"
                    className={classes.userName}
                    sx={{
                      color: theme.palette.mode === "dark" ? "text.primary" : "#fff",
                    }}
                  >
                    {currentUser.name || ""}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.userEmail}
                    sx={{
                      opacity: 0.72,
                      color: theme.palette.mode === "dark" ? "text.secondary" : "#fff",
                    }}
                  >
                    {isAdmin ? t("userAdmin") : isManager ? t("sharedRevenda") : currentUser.email}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <br />
          </Box>
          <Divider
            sx={{
              borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.12)",
            }}
          />

          <Box sx={{ px: 2, py: 0.5 }}>
            <GroupsList />
          </Box>
          <Divider
            sx={{
              borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.12)",
            }}
          />

          {!mapOnlyMode && (
            <MainToolbar
              filteredDevices={filteredDevices}
              devicesOpen={devicesOpen}
              setDevicesOpen={setDevicesOpen}
              mapOnlyMode={mapOnlyMode}
              setMapOnlyMode={setMapOnlyMode}
              keyword={keyword}
              setKeyword={setKeyword}
              filter={filter}
              setFilter={setFilter}
              filterSort={filterSort}
              setFilterSort={setFilterSort}
              filterMap={filterMap}
              setFilterMap={setFilterMap}
              searchFields={searchFields}
              setSearchFields={setSearchFields}
            />
          )}

          <Box sx={{ flexGrow: 1, p: 1 }}>
            <div className={classes.bottomMenu}>
              <BottomNavigation
                value={4}
                onChange={(event, newValue) => {
                  switch (newValue) {
                    case 0:
                      setFilter({ ...filter, statuses: ["online"] })
                      break
                    case 1:
                      setFilter({ ...filter, statuses: ["offline"] })
                      break
                    case 2:
                      setFilter({ ...filter, statuses: ["unknown"] })
                      break
                    default:
                      setFilter({ ...filter, statuses: [] })
                      break
                  }
                }}
                style={{ borderRadius: 10 }}
                showLabels
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "background.paper" : "rgba(255, 255, 255, 0.9)",
                }}
              >
                <BottomNavigationAction
                  className="success"
                  label={t("deviceStatusOnline")}
                  icon={
                    <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                      {Object.values(devices).filter((f) => f.status === "online").length}
                    </Typography>
                  }
                />
                <BottomNavigationAction
                  className="error"
                  label={t("deviceStatusOffline")}
                  icon={
                    <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                      {Object.values(devices).filter((f) => f.status === "offline").length}
                    </Typography>
                  }
                />
                <BottomNavigationAction
                  className="neutral"
                  label={t("deviceStatusUnknown")}
                  icon={
                    <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                      {Object.values(devices).filter((f) => f.status === "unknown").length}
                    </Typography>
                  }
                />
                <BottomNavigationAction
                  className="warning"
                  label={t("deviceStatusTotal")}
                  icon={
                    <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                      {Object.values(devices).length}
                    </Typography>
                  }
                />
              </BottomNavigation>
            </div>
          </Box>
        </Paper>
        <div className={classes.middle}>
          {!desktop && (
            <div className={classes.contentMap}>
              <MainMap
                filteredPositions={filteredPositions}
                selectedPosition={selectedPosition}
                onEventsClick={onEventsClick}
              />
            </div>
          )}
          <Paper
            square
            className={classes.contentList}
            style={devicesOpen && !mapOnlyMode ? {} : { visibility: "hidden" }}
            sx={{
              bgcolor: theme.palette.mode === "dark" ? "background.paper" : "rgba(255, 255, 255, 0.9)",
              overflow: "auto",
            }}
          >
            <DeviceList devices={filteredDevices} />
          </Paper>
        </div>
        {desktop && (
          <div className={classes.footer}>
            <BottomMenu />
          </div>
        )}
      </div>
      )}
  {mapOnlyMode && (
    <IconButton
      onClick={() => {
        setMapOnlyMode(false)
        setDevicesOpen(true)
      }}
      sx={(theme) => ({
        position: 'fixed',
        top: 16,
        left: 16, // ⬅️ Aqui trocamos 'right' por 'left'
        zIndex: 9999,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.85)'
            : 'rgba(255, 255, 255, 0.85)',
        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
        boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(60, 60, 60, 0.95)'
              : 'rgba(255, 255, 255, 1)',
          transform: 'scale(1.05)',
        },
        width: 48,
        height: 48,
      })}
    >
      <ViewListIcon fontSize="medium" />
    </IconButton>
  )}


      <EventsDrawer open={eventsOpen} onClose={() => setEventsOpen(false)} />
      {selectedDeviceId && (
        <StatusCard
          deviceId={selectedDeviceId}
          position={selectedPosition}
          onClose={() => dispatch(devicesActions.selectId(null))}
          desktopPadding={theme.dimensions.drawerWidthDesktop}
        />
      )}
    </div>
  )
}

export default MainPage